import { NumberField } from '@base-ui/react/number-field';
import clsx from 'clsx';
import { cva } from 'cva';
import { CircleAlertIcon } from 'lucide-react';
import { useId } from 'react';

const fieldStyles = cva({
  base: [
    'w-full rounded-lg border border-v1-neutral-40 bg-white typography-v1-body4',
    'placeholder:text-v1-neutral-40',
    'focus-within:border-v1-primary-100',
    'data-[invalid=true]:border-v1-others-red-40 data-[invalid=true]:focus-within:border-v1-others-red-40',
    'data-[disabled=true]:cursor-not-allowed data-[disabled=true]:bg-v1-background-20 data-[disabled=true]:text-v1-neutral-40',
  ],
});

const inputStyles = cva({
  base: 'min-w-0 flex-1 bg-transparent py-3 pl-4 outline-none disabled:cursor-not-allowed',
  variants: {
    appearance: {
      default: '',
      /** Hide native number-input spinners (Chrome/Safari/Firefox). */
      textfield: [
        '[appearance:textfield]',
        '[&::-webkit-inner-spin-button]:appearance-none',
        '[&::-webkit-outer-spin-button]:appearance-none',
      ],
    },
    hasEndAdornment: {
      true: 'pr-2',
      false: 'pr-4',
    },
  },
  defaultVariants: {
    appearance: 'default',
    hasEndAdornment: false,
  },
});

const FieldError = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <p
    id={id}
    className="-mb-1.5 rounded-t-lg bg-v1-others-red-10 px-4 pt-1.25 pb-2 typography-v1-caption1 font-semibold text-v1-others-red-40"
  >
    {children}
  </p>
);

export type InputProps = Omit<React.ComponentProps<'input'>, 'aria-invalid'> & {
  endAdornment?: React.ReactNode;
  error?: string;
  /** `textfield` hides the native spinner controls on `type="number"` inputs. */
  appearance?: 'textfield';
};

export const Input = ({
  className,
  endAdornment,
  error,
  disabled,
  appearance,
  ref,
  ...props
}: InputProps) => {
  const errorId = useId();

  endAdornment = error ? (
    <CircleAlertIcon className="size-5 text-v1-others-red-40" />
  ) : (
    endAdornment
  );

  return (
    <div className={clsx('flex w-full flex-col', className)}>
      {error && <FieldError id={errorId}>{error}</FieldError>}
      <div className={clsx(fieldStyles(), 'flex')} data-disabled={disabled} data-invalid={!!error}>
        <input
          ref={ref}
          className={inputStyles({ appearance, hasEndAdornment: !!endAdornment })}
          disabled={disabled}
          {...props}
          aria-invalid={!!error || undefined}
          aria-describedby={error ? errorId : undefined}
        />
        {endAdornment && <div className="flex shrink-0 items-center pr-4">{endAdornment}</div>}
      </div>
    </div>
  );
};

export type NumberInputProps = {
  id?: string;
  name?: string;
  value?: number | null;
  defaultValue?: number;
  onValueChange?: (value: number | null) => void;
  onValueCommitted?: (value: number | null) => void;
  min?: number;
  max?: number;
  step?: number | 'any';
  format?: Intl.NumberFormatOptions;
  /** Defaults to `nb-NO` so amounts render with Norwegian grouping. */
  locale?: Intl.LocalesArgument;
  endAdornment?: React.ReactNode;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  readOnly?: boolean;
  className?: string;
  placeholder?: string;
  ref?: React.Ref<HTMLInputElement>;
};

/**
 * Same chrome as {@link Input}, backed by Base UI `NumberField` for locale-aware
 * formatting and numeric commit semantics (blur / scrub / keyboard).
 */
export const NumberInput = ({
  className,
  endAdornment,
  error,
  disabled,
  value,
  defaultValue,
  onValueChange,
  onValueCommitted,
  min,
  max,
  step,
  format,
  locale = 'nb-NO',
  id,
  name,
  required,
  readOnly,
  placeholder,
  ref,
}: NumberInputProps) => {
  const errorId = useId();

  endAdornment = error ? (
    <CircleAlertIcon className="size-5 text-v1-others-red-40" />
  ) : (
    endAdornment
  );

  return (
    <div className={clsx('flex w-full flex-col', className)}>
      {error && <FieldError id={errorId}>{error}</FieldError>}
      <NumberField.Root
        id={id}
        name={name}
        value={value}
        defaultValue={defaultValue}
        onValueChange={onValueChange}
        onValueCommitted={onValueCommitted}
        min={min}
        max={max}
        step={step}
        format={format}
        locale={locale}
        disabled={disabled}
        required={required}
        readOnly={readOnly}
        className={clsx(fieldStyles(), 'flex')}
        data-disabled={disabled}
        data-invalid={!!error}
      >
        <NumberField.Input
          ref={ref}
          placeholder={placeholder}
          className={inputStyles({ appearance: 'textfield', hasEndAdornment: !!endAdornment })}
          aria-invalid={!!error || undefined}
          aria-describedby={error ? errorId : undefined}
        />
        {endAdornment && <div className="flex shrink-0 items-center pr-4">{endAdornment}</div>}
      </NumberField.Root>
    </div>
  );
};

export type TextareaProps = Omit<React.ComponentProps<'textarea'>, 'aria-invalid'> & {
  error?: string;
};

export const Textarea = ({ className, error, disabled, ref, ...props }: TextareaProps) => {
  const errorId = useId();

  return (
    <div className={clsx('flex w-full flex-col', className)}>
      {error && <FieldError id={errorId}>{error}</FieldError>}
      <textarea
        ref={ref}
        className={clsx(fieldStyles(), 'min-h-0 flex-1 resize-none px-4 py-3 outline-none')}
        data-disabled={disabled}
        data-invalid={!!error}
        disabled={disabled}
        {...props}
        aria-invalid={!!error || undefined}
        aria-describedby={error ? errorId : undefined}
      />
    </div>
  );
};
